import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
  TemplateRef,
  HostBinding,
} from '@angular/core';
import { MenuItem } from '@components/abb-actions-menu/abb-actions-menu.component';
import {
  DataTableFilter,
  FilterOption,
  IColumn,
} from '@components/abb-table/abb-table.component';
import { PAGES } from '@constants/constants';
import { LayoutComponent } from '@layout/layout.component';
import { Apollo } from 'apollo-angular';
import {
  ReportCategorySelectOptionsQuery,
  ReportCategorySelectOptionsQueryVariables,
  ReportListQuery,
  ReportExportQuery,
  ReportExportQueryVariables,
  UserSelectOptionsQuery,
  UserSelectOptionsQueryVariables,
  DeleteReportMutation,
  DeleteReportMutationVariables,
} from 'gql/graphql';
import { AbbCommonModule } from 'app/abb-common.module';
import { OverlayService } from '@services/ui/overlay.service';
import { BehaviorSubject, first, fromEvent, Subscription } from 'rxjs';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { ReportsSidePanelComponent } from './reports-side-panel/reports-side-panel.component';
import { ReportModalComponent } from './report-modal/report.modal.component';
import {
  reportCategorySelectOptionsQuery,
  reportListQuery,
  reportExportQuery,
  deleteReport,
  getRequestLTPayloadQuery,
  postHTMLpropertyLTMutation,
} from '@graphql/report';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/ui/toast.service';
import { dataProviderFromObservable } from '@utils/data-provider';
import { downloadBase64File } from '@utils/download';
import { userSelectOptionsQuery } from '@graphql/user';
import { AbbDateComponent } from '@components/abb-date/abb-date.component';
import { normalizeDate, normalizeDateSec } from '@utils/datetime';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';
import { LOADING } from '@utils/loader';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { PermissionsService } from '@services/user/permissions.service';
import { propertyLicenseTypeQuery } from '@graphql/property';
import { getLicenseIdQuery } from '@graphql/account';

export interface ReportDataTableFilter {
  name: string;
  date: {
    start: { date: Date | null; time: boolean };
    end: { date: Date | null; time: boolean };
  };
  userId: string;
  categoryId: string;
}

@Component({
  selector: 'abb-reports',
  standalone: true,
  template: `
    <page-layout [page]="PAGES.REPORTS" [title]="'Reports' | t">
      <div subtitle>
        <div>
          {{
            'You have a total of {tot} reports available.'
              | t : { tot: reportsCount() }
          }}
        </div>
      </div>
      @if(canEdit()){
      <div class="flex flex-row gap-2 justify-end w-full mb-4">
        <abb-button id="new-report" (onclick)="openSidePanel()">{{
          'New Report' | t
        }}</abb-button>
        @if(isLTLicense()) {
       <div>
       <form
       id="lookthroughForm"
      action="https://abb-api.lookthrough.com/api_esg.pl"
      method="post"
      target="_blank"
      (ngSubmit)="handleLookthroughSubmit($event)"
       >
      <input type="hidden" name="api_data" id="api_data">
      <abb-button color="accent" type="submit">{{ 'ESG report' | t }}</abb-button>
      </form>
      </div>
        }
      </div>
      }
      <abb-table
        [columns]="columns()"
        [dataProvider]="dataProvider"
        [loading]="loading()"
        [actions]="actions"
        [filters]="filters()"
        [defaultFilters]="defaultFilters"
        [defaultSortBy]="[
          { column: 'date', order: 'desc' },
          { column: 'name', order: 'asc' }
        ]"
      ></abb-table>
      <ng-template #date let-item>
        @if (item.date === null) {
        {{ '-' }}
        } @else {
        <abb-date
          [date]="item.date"
          [showTime]="true"
          [showTooltip]="true"
        ></abb-date>
        }
      </ng-template>
      <ng-template #author let-item>
        <span overflowTooltip class="block truncate"
          >{{ item.author.name }} {{ item.author.lastName }}
        </span>
      </ng-template>
      <ng-template #category let-item>
        <span overflowTooltip class="block truncate">{{
          item.category.name
        }}</span>
      </ng-template>
    </page-layout>
  `,
  styles: ``,
  host: {
    class: 'flex-1',
  },
  imports: [
    LayoutComponent,
    CommonModule,
    FormsModule,
    AbbCommonModule,
    TPipe,
    AbbDateComponent,
    OverflowTooltipDirective,
  ],
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'reports',
    },
  ],
})
export class ReportsComponent implements OnDestroy, OnInit {
    async handleLookthroughSubmit(e: Event) {
      e.preventDefault();
      this.awaitingRes.set(true);
      const encoder = new TextEncoder();
      const querySub = this.apollo
        .query({ query: getRequestLTPayloadQuery })
        .subscribe({
          next: async (result: any) => {
            const getRequestLTPayload = result.data?.getRequestLTPayload;
            const basePayloadStr = getRequestLTPayload?.RequestLTPayload;
            let basePayload: any = undefined;
            try {
              basePayload = basePayloadStr ? JSON.parse(basePayloadStr) : undefined;
            } catch (e) {
              console.log('An error occurred');
            }
            if (!basePayload) {
              this.awaitingRes.set(false);
              console.log('Error: payload not available');
              return;
            }
            if (getRequestLTPayload?.allPropertiesLTValid === false) {
              this.awaitingRes.set(false);
              this.overlayService.openAlert({
                message: t(this.prefix, 'Not all values for the properties are completed', this.translate),
                showCancelButtonRed: true,
                title: t(this.prefix, 'Attention', this.translate),
                showCancelButton: false,
                showConfirmButton: false,
              });
              return;
            }
            try {
              const payload = {
                ...basePayload,
              };
              const apiDataInput = document.getElementById("api_data") as HTMLInputElement;
              if (apiDataInput) {
                apiDataInput.value = JSON.stringify(basePayload);
              }
              (e.target as HTMLFormElement).submit();
          
              setTimeout(() => this.awaitingRes.set(false), 200);
            } catch (err) {
         
              this.awaitingRes.set(false);
            }
            querySub.unsubscribe();
          },
          error: (err) => {
            this.awaitingRes.set(false);
          },
        });
      this.subscriptions.push(querySub);
    }
  @HostBinding('attr.id') componentid = 'reports-component';
  readonly PAGES: PAGES = PAGES;
  reports$ = new BehaviorSubject<ReportListQuery['reportList'] | []>([]);
  reportsCount = signal(0);
  dateTemplate = viewChild<TemplateRef<unknown>>('date');
  authorTemplate = viewChild<TemplateRef<unknown>>('author');
  categoryTemplate = viewChild<TemplateRef<unknown>>('category');
  canEdit = computed(() => this.permissionsService.canEditPage(PAGES.REPORTS));
  isLTLicense = signal<boolean>(false);
  private isLTLicenseLoaded = false;

  userAutocompleteOptions = signal<FilterOption[]>([]);
  categorySelectOptions = signal<FilterOption[]>([]);
  filters = computed<DataTableFilter<ReportDataTableFilter>[]>(() => {
    return [
      {
        id: 'name',
        filterType: 'text',
        className: 'w-full',
        label: 'Report Name',
      },
      {
        id: 'date',
        filterType: 'datetimeRange',
        className: 'w-full',
        label: 'Date',
      },
      {
        id: 'userId',
        filterType: 'autocomplete',
        filterOptions: this.userAutocompleteOptions(),
        className: 'w-full',
        label: 'Author',
      },
      {
        id: 'categoryId',
        filterOptions: this.categorySelectOptions(),
        filterType: 'select',
        className: 'w-full',
        label: 'Category',
      },
    ];
  });
  defaultFilters: ReportDataTableFilter = {
    userId: '',
    categoryId: '',
    date: {
      start: {
        date: null,
        time: false,
      },
      end: {
        date: null,
        time: false,
      },
    },
    name: '',
  };
  dataProvider = dataProviderFromObservable<
    ReportListQuery['reportList'][number],
    ReportDataTableFilter
  >(
    this.reports$,
    (el, filter) => {
      return (
        (!filter.userId || el.author.id === filter.userId) &&
        (!filter.categoryId || el.category.id === filter.categoryId) &&
        (!filter.date?.start.date ||
          (filter.date.start.time
            ? filter.date.end.date
              ? normalizeDateSec(el.date) >=
                normalizeDateSec(filter.date?.start.date)
              : normalizeDateSec(el.date) ===
                normalizeDateSec(filter.date?.start.date)
            : filter.date.end.date
            ? normalizeDate(el.date) >= normalizeDate(filter.date.start.date)
            : normalizeDate(el.date) ===
              normalizeDate(filter.date.start.date))) &&
        (!filter.date?.end.date ||
          (filter.date.end.time
            ? normalizeDateSec(el.date) <=
              normalizeDateSec(filter.date.end.date)
            : normalizeDate(el.date) <= normalizeDate(filter.date.end.date))) &&
        el.name.toLowerCase().includes(filter.name.toLowerCase())
      );
    },
    (a, b, cols) => {
      for (let c of cols) {
        let itemA, itemB;
        if (c.column === 'category') {
          itemA = a.category.name.toLowerCase();
          itemB = b.category.name.toLowerCase();
        } else if (c.column === 'author') {
          itemA = `${a.author.name} ${a.author.lastName}`.toLowerCase();
          itemB = `${b.author.name} ${b.author.lastName}`.toLowerCase();
        } else {
          const key = c.column as keyof Omit<
            ReportListQuery['reportList'][number],
            'author' | 'category' | '__typename'
          >;
          itemA =
            a[key] instanceof Date ? a[key] : (a[key] as string)?.toLowerCase();
          itemB =
            b[key] instanceof Date ? b[key] : (b[key] as string)?.toLowerCase();
        }

        if (itemA < itemB) {
          return c.order === 'asc' ? -1 : 1;
        }
        if (itemA > itemB) {
          return c.order === 'asc' ? 1 : -1;
        }
      }
      return 0;
    }
  );

  columns = computed<IColumn<ReportListQuery['reportList'][number]>[]>(() => {
    return [
      {
        id: 'id',
        name: 'Id',
        key: 'id',
        translate: true,
        hidden: true,
      },
      {
        id: 'name',
        name: 'Name',
        key: 'name',
        translate: true,
        tableCellClass: 'font-semibold',
      },
      {
        id: 'date',
        name: 'Date',
        templateRef: this.dateTemplate(),
        translate: true,
        sortable: true,
      },
      {
        id: 'author',
        name: 'Author',
        templateRef: this.authorTemplate(),
        translate: true,
        tableCellClass: 'font-semibold',
      },
      {
        id: 'category',
        name: 'Category',
        templateRef: this.categoryTemplate(),
        translate: true,
      },
      {
        id: 'format',
        name: 'Format',
        key: 'format',
        translate: true,
        tableCellClass: 'font-semibold',
      },
      {
        name: 'actions',
      },
    ];
  });
  loading = signal(true);

  prefix = inject(TRANSLATION_PREFIX);
  private subscriptions: Subscription[] = [];
  constructor(
    private readonly overlayService: OverlayService,
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
    private readonly permissionsService: PermissionsService,
    private readonly http: HttpClient
  ) {
    this.subscriptions.push(
      fromEvent(window, 'focus').subscribe(() =>
        this.apollo.client.refetchQueries({
          include: ['ReportList'],
        })
      ),
      apollo
        .watchQuery<ReportListQuery>({
          query: reportListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data, loading }) => {
            this.reports$.next(data.reportList);
            this.loading.set(loading);
            this.reportsCount.set(data.reportList.length);
          },
          error: (err) => {
            this.loading.set(false);
          },
        }),
      apollo
        .watchQuery<UserSelectOptionsQuery, UserSelectOptionsQueryVariables>({
          query: userSelectOptionsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.userAutocompleteOptions.set(
              data.userList.map((user) => ({
                value: user.id,
                label: `${user.name} ${user.lastName}`,
                skipTranslation: true,
              }))
            );
          },
          error: (err) => {
          },
        }),
      apollo
        .watchQuery<
          ReportCategorySelectOptionsQuery,
          ReportCategorySelectOptionsQueryVariables
        >({ query: reportCategorySelectOptionsQuery })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.categorySelectOptions.set(
              data.reportCategoryList.map((cat) => ({
                value: cat.id,
                label: cat.name,
                skipTranslation: true,
              }))
            );
          },
          error: (err) => {
          },
        })
    );
  }

  ngOnInit() {
    if (!this.isLTLicenseLoaded) {
      this.subscriptions.push(
        this.apollo
          .watchQuery<any>({
            query: getLicenseIdQuery,
          })
          .valueChanges.pipe(first())
          .subscribe({
            next: (result) => {
              const licenseId = result.data?.getLicenseId ?? '';
              if (licenseId) {
                this.subscriptions.push(
                  this.apollo
                    .watchQuery<any>({
                      query: propertyLicenseTypeQuery,
                      variables: { id: licenseId },
                    })
                    .valueChanges.pipe(first())
                    .subscribe({
                      next: (result) => {
                        this.isLTLicense.set(
                          result.data?.propertyLicenseType?.isLTLicense ?? false
                        );
                        this.isLTLicenseLoaded = true;
                      },
                      error: () => {
                        this.isLTLicense.set(false);
                        this.isLTLicenseLoaded = true;
                      },
                    })
                );
              } else {
                this.isLTLicense.set(false);
                this.isLTLicenseLoaded = true;
              }
            },
            error: () => {
              this.isLTLicense.set(false);
              this.isLTLicenseLoaded = true;
            },
          })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
  
  actions: Array<MenuItem<ReportListQuery['reportList'][number]>> = [
    {
      icon: 'mat/open_in_new',
      label: 'Open',
      action: (reports) => this.openReportModal(reports![0]),
    },
    {
      icon: 'abb/download',
      label: 'Download',
      action: (reports) => this.downloadFile(reports![0]),
    },
    {
      icon: 'mat/delete_outline',
      label: 'Remove',
      action: (reports) => this.openDeleteModal(reports![0]),
    },
  ];

  awaitingRes = inject(LOADING);
  getReport(id: string, callback: (data: ReportExportQuery) => void) {
    this.subscriptions.push(
      this.apollo
        .query<ReportExportQuery, ReportExportQueryVariables>({
          query: reportExportQuery,
          variables: {
            id,
          },
        })
        .pipe(first())
        .subscribe({
          next: ({ data }) => {
            callback(data);
          },
          error: () => {
            this.awaitingRes.set(false);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while exporting file`,
                this.translate
              )
            );
          },
        })
    );
  }
 
  openReportModal(report: ReportListQuery['reportList'][number]) {
    this.awaitingRes.set(true);
    this.getReport(report.id, (data: ReportExportQuery) => {
      this.awaitingRes.set(false);
      this.overlayService.openModal(ReportModalComponent, {
        id: data.reportExport.id,
        filename: data.reportExport.filename,
        contentType: data.reportExport.contentType,
        size: data.reportExport.size,
        content: data.reportExport.content,
      });
    });
  }

  downloadFile(report: ReportListQuery['reportList'][number]) {
    this.awaitingRes.set(true);
    this.getReport(report.id, (data: ReportExportQuery) => {
      downloadBase64File(data.reportExport);
      this.awaitingRes.set(false);
      this.toast.notifySuccess(
        t(this.prefix, `File exported successfully`, this.translate)
      );
    });
  }

  removeReport(report: ReportListQuery['reportList'][number]) {
    this.awaitingRes.set(true);
    return new Promise<void>((resolve, reject) => {
      this.apollo
        .mutate<DeleteReportMutation, DeleteReportMutationVariables>({
          mutation: deleteReport,
          variables: {
            id: report.id,
          },
        })
        .subscribe({
          next: () => {
            this.awaitingRes.set(false);
            this.toast.notifySuccess(
              t(this.prefix, `Report removed successfully`, this.translate)
            );
            resolve();
          },
          error: (err) => {
            this.awaitingRes.set(false);
            const errMsg = getErrorMessage(err, this.translate);
            this.toast.notifyError(errMsg);
            reject(errMsg);
          },
        });
    });
  }

  openDeleteModal(report: ReportListQuery['reportList'][number]) {
    this.overlayService
      .openAlert({
        message: `${t(
          this.prefix,
          'Are you sure you want to remove report',
          this.translate
        )} <b>${report.name!}</b>?`,
        htmlMessage: true,
        confirmText: t(this.prefix, 'Remove', this.translate),
      })
      .then(() => {
        this.awaitingRes.set(true);
        this.removeReport(report)
          .then(() => {
            this.apollo.client.refetchQueries({
              include: ['ReportList'],
            });
          })
          .catch((err) => {
            console.error('Error removing report:', err);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while removing report`,
                this.translate
              )
            );
            this.awaitingRes.set(false);
          });
      })
      .catch(() => console.log('cancel'));
  }

  openSidePanel(): void {
    this.overlayService.openSidePanel(ReportsSidePanelComponent, {
      mode: 'new',
    });
  }
}
